import { useState } from "react";
import tagData from "./tagData";
import "./App.css";

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const TagIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export default function App() {
  const [l1, setL1] = useState("");
  const [l2, setL2] = useState("");
  const [l3, setL3] = useState("");
  const [result, setResult] = useState(null);
  const [generated, setGenerated] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const l1Options = Object.keys(tagData);
  const l2Options = l1 ? Object.keys(tagData[l1]) : [];
  const l3Options = l1 && l2 ? Object.keys(tagData[l1][l2]) : [];

  const handleL1Change = (e) => {
    setL1(e.target.value);
    setL2("");
    setL3("");
    setResult(null);
    setGenerated(false);
  };

  const handleL2Change = (e) => {
    setL2(e.target.value);
    setL3("");
    setResult(null);
    setGenerated(false);
  };

  const handleL3Change = (e) => {
    setL3(e.target.value);
    setResult(null);
    setGenerated(false);
  };

  const handleGenerate = () => {
    if (l1 && l2 && l3) {
      const data = tagData[l1][l2][l3];
      setResult(data);
      setGenerated(true);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleReset = () => {
    setL1("");
    setL2("");
    setL3("");
    setResult(null);
    setGenerated(false);
  };

  const isComplete = l1 && l2 && l3;

  return (
    <div className="app">
      {/* Background decoration */}
      <div className="bg-decoration">
        <div className="bg-orb bg-orb-1" />
        <div className="bg-orb bg-orb-2" />
        <div className="bg-orb bg-orb-3" />
      </div>

      <div className="container">
        {/* Header */}
        <header className="header">
          <div className="header-icon">
            <TagIcon />
          </div>
          <div className="header-text">
            <h1 className="header-title">Tag Generator</h1>
            <p className="header-subtitle">Select categories to generate product tags instantly</p>
          </div>
        </header>

        {/* Main Card */}
        <div className="card">
          {/* Progress dots */}
          <div className="progress-track">
            <div className={`progress-step ${l1 ? "active" : ""}`}>
              <div className="step-dot">{l1 ? <CheckIcon /> : "1"}</div>
              <span className="step-label">L1 Category</span>
            </div>
            <div className={`progress-line ${l2 ? "active" : ""}`} />
            <div className={`progress-step ${l2 ? "active" : ""}`}>
              <div className="step-dot">{l2 ? <CheckIcon /> : "2"}</div>
              <span className="step-label">L2 Category</span>
            </div>
            <div className={`progress-line ${l3 ? "active" : ""}`} />
            <div className={`progress-step ${l3 ? "active" : ""}`}>
              <div className="step-dot">{l3 ? <CheckIcon /> : "3"}</div>
              <span className="step-label">L3 Category</span>
            </div>
          </div>

          {/* Dropdowns */}
          <div className="dropdowns">
            {/* L1 */}
            <div className="field">
              <label className="field-label">
                <span className="field-badge">L1</span>
                L1 Category
              </label>
              <div className="select-wrapper">
                <select className="select" value={l1} onChange={handleL1Change}>
                  <option value="">— Select L1 Category —</option>
                  {l1Options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <span className="select-arrow"><ChevronIcon /></span>
              </div>
            </div>

            {/* L2 */}
            <div className={`field ${!l1 ? "field-disabled" : ""}`}>
              <label className="field-label">
                <span className="field-badge">L2</span>
                L2 Category
              </label>
              <div className="select-wrapper">
                <select className="select" value={l2} onChange={handleL2Change} disabled={!l1}>
                  <option value="">— Select L2 Category —</option>
                  {l2Options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <span className="select-arrow"><ChevronIcon /></span>
              </div>
            </div>

            {/* L3 */}
            <div className={`field ${!l2 ? "field-disabled" : ""}`}>
              <label className="field-label">
                <span className="field-badge">L3</span>
                L3 Category
              </label>
              <div className="select-wrapper">
                <select className="select" value={l3} onChange={handleL3Change} disabled={!l2}>
                  <option value="">— Select L3 Category —</option>
                  {l3Options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <span className="select-arrow"><ChevronIcon /></span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="actions">
            <button
              className={`btn-generate ${isComplete ? "btn-active" : "btn-inactive"}`}
              onClick={handleGenerate}
              disabled={!isComplete}
            >
              <SparkleIcon />
              Generate Tags
            </button>
            {generated && (
              <button className="btn-reset" onClick={handleReset}>
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        {generated && result && (
          <div className="results-card">
            <div className="results-header">
              <h2 className="results-title">Generated Tags</h2>
              <div className="results-path">
                <span>{l1}</span>
                <span className="path-sep">›</span>
                <span>{l2}</span>
                <span className="path-sep">›</span>
                <span className="path-current">{l3}</span>
              </div>
            </div>

            <div className="tag-fields">
              {/* Category Tags */}
              <div className="tag-field">
                <div className="tag-field-header">
                  <span className="tag-field-label">Category Tags</span>
                </div>
                <div className="tag-value-row">
                  <div className="tag-value">
                    {result.categoryTags
                      ? result.categoryTags.split(",").map((tag, i) => (
                          <span key={i} className="tag-chip">{tag.trim()}</span>
                        ))
                      : <span className="tag-empty">No category tags defined</span>
                    }
                  </div>
                  {result.categoryTags && (
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(result.categoryTags, "category")}
                      title="Copy to clipboard"
                    >
                      {copiedField === "category" ? <CheckIcon /> : <CopyIcon />}
                      {copiedField === "category" ? "Copied!" : "Copy"}
                    </button>
                  )}
                </div>
              </div>

              {/* Type Tag */}
              <div className="tag-field">
                <div className="tag-field-header">
                  <span className="tag-field-label">Type Tag</span>
                </div>
                <div className="tag-value-row">
                  <div className="tag-value">
                    {result.typeTag
                      ? <span className="tag-chip tag-chip-type">{result.typeTag}</span>
                      : <span className="tag-empty">No type tag defined</span>
                    }
                  </div>
                  {result.typeTag && (
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(result.typeTag, "type")}
                      title="Copy to clipboard"
                    >
                      {copiedField === "type" ? <CheckIcon /> : <CopyIcon />}
                      {copiedField === "type" ? "Copied!" : "Copy"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Copy All */}
            {(result.categoryTags || result.typeTag) && (
              <button
                className="btn-copy-all"
                onClick={() =>
                  handleCopy(
                    [result.categoryTags, result.typeTag].filter(Boolean).join(", "),
                    "all"
                  )
                }
              >
                {copiedField === "all" ? <CheckIcon /> : <CopyIcon />}
                {copiedField === "all" ? "All Copied!" : "Copy All Tags"}
              </button>
            )}
          </div>
        )}

        <footer className="footer">
          <p>TagGenerator · Powered by Tag Logic Data</p>
        </footer>
      </div>
    </div>
  );
}
